import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

type Dot = {
    x: number
    y: number
    heat: number
    fraud: boolean
}

interface SignalFieldProps {
    className?: string
    accent?: string
    alert?: string
    gap?: number
}

// A field of data points, one per transaction. Random points light up as
// decisions come in (a few flag red), a scan line sweeps across, and the
// points near the cursor brighten and lean away from it. Colours follow the
// current tone through the canvas' own text colour.
export const SignalField: FC<SignalFieldProps> = ({ className, accent = '#8c5fff', alert = '#f92468', gap = 26 }): ReactNode => {
    const ref = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const canvas = ref.current
        const context = canvas?.getContext('2d')

        if (!canvas || !context) return

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        let width = 0
        let height = 0
        let dots: Dot[] = []
        let base = getComputedStyle(canvas).color
        let pointerX = -9999
        let pointerY = -9999
        let frame = 0
        let visible = true
        let lastColorRead = 0

        const build = () => {
            const rect = canvas.getBoundingClientRect()
            const ratio = Math.min(2, window.devicePixelRatio || 1)

            width = rect.width
            height = rect.height
            canvas.width = Math.round(width * ratio)
            canvas.height = Math.round(height * ratio)
            context.setTransform(ratio, 0, 0, ratio, 0, 0)

            const columns = Math.ceil(width / gap) + 1
            const rows = Math.ceil(height / gap) + 1
            const offsetX = (width - (columns - 1) * gap) / 2
            const offsetY = (height - (rows - 1) * gap) / 2

            dots = []

            for (let row = 0; row < rows; row++) {
                for (let column = 0; column < columns; column++) {
                    dots.push({ x: offsetX + column * gap, y: offsetY + row * gap, heat: 0, fraud: false })
                }
            }
        }

        const draw = (time: number) => {
            if (time - lastColorRead > 400) {
                base = getComputedStyle(canvas).color
                lastColorRead = time
            }

            context.clearRect(0, 0, width, height)

            if (!reduce && dots.length > 0) {
                for (let spawn = 0; spawn < 2; spawn++) {
                    if (Math.random() < 0.55) {
                        const dot = dots[Math.floor(Math.random() * dots.length)]

                        dot.heat = 1
                        dot.fraud = Math.random() < 0.09
                    }
                }
            }

            const scanX = reduce ? -9999 : ((time / 7000) % 1) * (width + 400) - 200

            if (pointerX > -9000) {
                const glow = context.createRadialGradient(pointerX, pointerY, 0, pointerX, pointerY, 260)

                glow.addColorStop(0, `${accent}33`)
                glow.addColorStop(1, `${accent}00`)
                context.fillStyle = glow
                context.fillRect(pointerX - 260, pointerY - 260, 520, 520)
            }

            for (const dot of dots) {
                const dx = dot.x - pointerX
                const dy = dot.y - pointerY
                const distance = Math.sqrt(dx * dx + dy * dy) || 1
                const near = Math.max(0, 1 - distance / 190)
                const scan = Math.max(0, 1 - Math.abs(dot.x - scanX) / 70)
                const push = near * near * 12
                const x = dot.x + (dx / distance) * push
                const y = dot.y + (dy / distance) * push
                const size = 1.4 + near * 2.2 + dot.heat * 2.2

                if (dot.heat > 0.05) {
                    context.fillStyle = dot.fraud ? alert : accent
                    context.globalAlpha = Math.min(1, 0.25 + dot.heat)
                }
                else if (near > 0.25) {
                    context.fillStyle = accent
                    context.globalAlpha = 0.25 + near * 0.75
                }
                else {
                    context.fillStyle = base
                    context.globalAlpha = 0.1 + near * 0.4 + scan * 0.35
                }

                context.fillRect(x - size / 2, y - size / 2, size, size)
                dot.heat *= 0.955
            }

            context.globalAlpha = 1
        }

        const loop = (time: number) => {
            frame = 0

            if (!visible || document.hidden) return

            draw(time)
            frame = requestAnimationFrame(loop)
        }

        const start = () => {
            if (!frame && !reduce) frame = requestAnimationFrame(loop)
        }

        const onPointer = (event: PointerEvent) => {
            const rect = canvas.getBoundingClientRect()

            pointerX = event.clientX - rect.left
            pointerY = event.clientY - rect.top
        }

        const onLeave = () => {
            pointerX = -9999
            pointerY = -9999
        }

        build()
        draw(0)

        const resize = new ResizeObserver(() => {
            build()
            draw(performance.now())
        })

        const view = new IntersectionObserver(([entry]) => {
            visible = entry.isIntersecting

            if (visible) start()
        })

        const onVisibility = () => {
            if (!document.hidden) start()
        }

        resize.observe(canvas)
        view.observe(canvas)
        window.addEventListener('pointermove', onPointer, { passive: true })
        document.documentElement.addEventListener('pointerleave', onLeave)
        document.addEventListener('visibilitychange', onVisibility)
        start()

        return () => {
            cancelAnimationFrame(frame)
            resize.disconnect()
            view.disconnect()
            window.removeEventListener('pointermove', onPointer)
            document.documentElement.removeEventListener('pointerleave', onLeave)
            document.removeEventListener('visibilitychange', onVisibility)
        }
    }, [accent, alert, gap])

    return (
        <canvas
            ref={ref}
            aria-hidden
            className={cn('pointer-events-none absolute inset-0 size-full text-tone', className)}
        />
    )
}
